import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {  searchCustomer } from "src/apis/customerApi";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";


import { BRDiv, titleMRClass } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { defaultWMS1140_R01HeaderDto, WMS1140_R01HeaderDto, } from "./Dto_WMS1140_R01";
import { useWMS1140_R01Store } from "./Store_WMS1140_R01";
import {
	normalizeWMS1140DetailRow,
	WMS1140_INOUT_CLASS_SUBCONTRACT_MATERIAL,
} from "./Constants_WMS1140_R01";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { getEmployeeList } from "src/apis/employeeApi";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { CheckboxButtonForm } from "src/components/common/form/checkbox/CheckboxButtonForm";
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";



interface Props {
	editable: boolean
	getWms1140InoutClassList: () => SysCodeDto[];
	defaultWMS1140_R01HeaderRow: WMS1140_R01HeaderDto;
}

export const Info_WMS1140_R01 = (props: Props) => {

	const { 
		editable,
		getWms1140InoutClassList,
		defaultWMS1140_R01HeaderRow,
	} = props;

	// 
	const { wmsCdList, compCdList, } = useCommonCodeStore();
	const { setCodeSearchModalProps, setAddressModalProps } = useUiuxStore();
	const { 
			editWMS1140_R01, setEditWMS1140_R01,
			WMS1140_R01DetailList, setWMS1140_R01DetailList,
			deletedWMS1140_R01DetailList,
			warehouseCdListToUseList,
	} = useWMS1140_R01Store();


	const custInfoHandleFoucsId = 'infoCustInput';
	const empInfoHandleFoucsId = 'infoEmpInput';
	const addressHandleFoucsId = 'addressInput2';

	// Info 폼 코드 검색 필드 설정
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'customCd',
			category: '거래처',
			focusId: custInfoHandleFoucsId,
			responseMapping: {
				customCd: 'customCd',
				customNm: 'customNm',
				telNo: 'telNo',
				faxNo: 'faxNo',
				moneyCd: 'moneyCd',
				taxEmail: 'email',
				post: 'post',
				address: 'address',
				detailAddress: 'otherAddress',
			},
			searchFunction: async (searchText: string) => {
				const customerResponse = await searchCustomer(searchText);
				return customerResponse.data.data.find(value => value.customCd === searchText);
			},
			extraFilter: () => ({
				customCd: editWMS1140_R01?.customCd || '',
				customNm: editWMS1140_R01?.customNm || '',
			}),
		},
		{
			fieldName: 'empNo',
			category: '담당자',
			focusId: empInfoHandleFoucsId,
			responseMapping: {
				empNo: 'empNo',
				empNm: 'empNm'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find(value => value.empNo === code);
			},
			extraFilter: () => ({
				empNo: editWMS1140_R01?.empNo || '',
				empNm: editWMS1140_R01?.empNm || '',
			}),
		}
	];

	// Info 폼 이벤트 훅 사용
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editWMS1140_R01,
		setEditData: setEditWMS1140_R01,
		defaultData: defaultWMS1140_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'WMS1140_R01'
	});

	// 입고일자 달력 팝업 상태
	const [orderPickerVisible, setOrderPickerVisible] = useState(false);
	const [orderPickerPos, setOrderPickerPos] = useState<{top: number, left: number, height: number}>({top: 0, left: 0, height: 0});

	const handleDateChange = useCallback((field: keyof WMS1140_R01HeaderDto, value: any) => {
		setEditWMS1140_R01({
			...defaultWMS1140_R01HeaderRow,
			...editWMS1140_R01,
			[field]: value
		});
	}, [editWMS1140_R01]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		let additionalUpdates = {};
		// 입고물류(logicCenter)가 변경될 때, 해당 물류의 첫 번째 창고로 warehouseCd 자동 세팅
		if (name === 'logicCenter') {
			const availableWarehouses = warehouseCdListToUseList(value);
			additionalUpdates = {
				warehouseCd: availableWarehouses[0]?.warehouseCd || ''
			};
		}

		setEditWMS1140_R01({
			...defaultWMS1140_R01HeaderRow,
			...editWMS1140_R01,
			[name]: value,
			...additionalUpdates
		});

		// 사급자재입고(170): 상세 단가·금액 0으로 초기화
		if (name === 'inoutClass' && value === WMS1140_INOUT_CLASS_SUBCONTRACT_MATERIAL) {
			setWMS1140_R01DetailList(
				WMS1140_R01DetailList.map((row) => normalizeWMS1140DetailRow(row, value)),
			);
		}
	}, [editWMS1140_R01, defaultWMS1140_R01HeaderRow, warehouseCdListToUseList, WMS1140_R01DetailList, setWMS1140_R01DetailList]);

	/** 입고수불번호가 없을 때만 헤더 필드 편집 허용 */
	const infoEditable = useCallback((): boolean => {
		return !validateString(editWMS1140_R01?.transportNo || '');
	}, [editWMS1140_R01?.transportNo]);

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300">
				<InputForm title="입고수불번호" value={editWMS1140_R01?.transportNo || ""} 
					editable={false}
					name="transportNo" 
					onChange={handleChange} />
				<BRDiv />

				<DropDownForm
					title="사업장"
					name="compCd"
					initLabelLess={true}
					value={editWMS1140_R01?.compCd}
					data={compCdList}
					editable={infoEditable()}
					onChange={handleChange}
				/>
				<SingleDateForm
					title="입고일자"
					value={editWMS1140_R01?.transportDt ? new Date(editWMS1140_R01.transportDt) : undefined}
					setValue={(value: Date | undefined) => handleDateChange('transportDt', formatDate(value))}
					editable={infoEditable()}
					visible={orderPickerVisible}				setVisible={setOrderPickerVisible}
					onChange={handleChange}
					pickerPos={orderPickerPos}					setPickerPos={setOrderPickerPos}
				/>
				<BRDiv />


				<DropDownForm
					title="입고구분"
					name="inoutClass"
					initLabelLess={true}
					value={editWMS1140_R01?.inoutClass}
					data={getWms1140InoutClassList()}
					editable={infoEditable()}
					onChange={handleChange}
				/>
				<BRDiv />

				<CodeForm
					label="거래처"
					value={editWMS1140_R01?.customCd || ""}
					custNmValue={editWMS1140_R01?.customNm || ""}
					editable={infoEditable()}
					handleFoucsId={custInfoHandleFoucsId}
					name="customCd"
					handleChange={getFieldHandlers('customCd').onChange}
					onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
					onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
				/>
				<CodeForm
					label="입고담당자"
					value={editWMS1140_R01?.empNo || ""}
					custNmValue={editWMS1140_R01?.empNm || ""}
					editable={infoEditable()}
					handleFoucsId={empInfoHandleFoucsId}
					name="empNo"
					handleChange={getFieldHandlers('empNo').onChange}
					onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
					onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
				/>
				<BRDiv />
				
				<DropDownForm<SysCodeDto>
						title="입고물류"
						data={wmsCdList}
						value={editWMS1140_R01?.logicCenter || ""}
						name="logicCenter" 
						editable={infoEditable()}
						initLabelLess={true}
						onChange={handleChange} />
				<DropDownForm
					title="창고번호"
					name="warehouseCd"
					initLabelLess={true}
					value={editWMS1140_R01?.warehouseCd}
					data={warehouseCdListToUseList(editWMS1140_R01?.logicCenter || '')}
					editable={infoEditable()}
					onChange={handleChange}
				/>
				<BRDiv />
				
				<InputForm
					title="비고"
					value={editWMS1140_R01?.remark || ""}
					editable={infoEditable()}
					name="remark"
					wrapperOption={{
						wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[10px]",
						titleStyle: "w-[100px] min-w-[100px] text-left",
						inputStyle: "w-full min-w-full"
					}}
					onChange={handleChange}
				/>
				
		</div>
	)
}